import { useMemo, useCallback } from 'react';
import {
  RBAC,
  userRolesByFeature,
  useLegalEntities,
  caseCodesTranslationKeys,
  settingsTranslationKeys,
  travelerPersonalTranslationKeys,
} from 'obt-common';
import * as Icons from '@spotnana/pixel-react/dist/Icons';
import Box from '@spotnana/pixel-react/dist/Box';
import Typography from '@spotnana/pixel-react/dist/Typography';
import spotnanaTheme from '@spotnana/pixel-react/dist/utils/themes/theme';
import { Button } from '@spotnana/pixel-react/dist/Button';
import { useTranslation } from 'react-i18next';
import LegalEntitiesSelect from '../../../../v2-components/shared/LegalEntitiesSelect';

const { CLEAR_ALL } = caseCodesTranslationKeys;
const { LEGAL_ENTITIES } = settingsTranslationKeys;
const { CREDIT_CARD_LEGAL_ENTITY_HELP } = travelerPersonalTranslationKeys;

export interface ISelectedItems {
  selectedItemIds: string[];
  areAllItemsSelected: boolean;
}

interface ISelectedLegalEntityProps {
  name: string;
  onRemove: () => void;
}

const SelectedLegalEntity = ({ name, onRemove }: ISelectedLegalEntityProps): JSX.Element => (
  <Box
    display="inline-flex"
    py="5px"
    px="8px"
    bg="bg.blueGray"
    border="thinSolid"
    borderColor="border.blockedGrey"
    borderRadius={18}
    alignItems="center"
    mt={2}
    mr={2}
  >
    <Typography>{name}</Typography>
    <Box ml={2} />
    <Icons.Close
      data-testid={`${name}-on-remove`}
      width={10}
      height={10}
      primaryColor={spotnanaTheme.colors.text.kashmirBlue}
      onClick={onRemove}
    />
  </Box>
);

type LegalEntityFormProps = Pick<ILegalEntityFormProps, 'itemIds' | 'setItemIds'> & {
  organizationId: string;
};

const LegalEntityForm = ({ itemIds, setItemIds, organizationId }: LegalEntityFormProps): JSX.Element => {
  const { t: tCommon } = useTranslation('COMMON');
  const { t: tAdmin } = useTranslation('ADMIN');
  const { t: tProfile } = useTranslation('PROFILE');
  const { legalEntities } = useLegalEntities({ organizationId });

  const options = useMemo(
    () =>
      legalEntities.reduce(
        (acc, legalEntity) => ({
          ...acc,
          [legalEntity.id.id]: legalEntity.name,
        }),
        {} as Record<string, string>,
      ),
    [legalEntities],
  );

  const handleRemoveItem = useCallback(
    (itemId: string) => (): void =>
      setItemIds({
        selectedItemIds: itemIds.filter((selectedItemId) => selectedItemId !== itemId),
        areAllItemsSelected: false,
      }),
    [itemIds, setItemIds],
  );

  const handleSetItem = useCallback(
    (selectedItemIds: string[]): void =>
      setItemIds({
        selectedItemIds,
        areAllItemsSelected: selectedItemIds.length === legalEntities.length,
      }),
    [setItemIds, legalEntities],
  );

  const handleClearAll = useCallback(
    (): void =>
      setItemIds({
        selectedItemIds: [],
        areAllItemsSelected: false,
      }),
    [setItemIds],
  );

  return (
    <>
      <Box mt={5} />
      <Typography variation="label" fontWeight="semiBold">
        {tAdmin(LEGAL_ENTITIES)}
      </Typography>
      <Box mt={3} />
      <LegalEntitiesSelect
        organizationId={organizationId}
        selectedItemIds={itemIds}
        setSelectedItemIds={handleSetItem}
      />
      {itemIds.length > 0 && (
        <Box display="inline-flex" flexWrap="wrap" alignItems="center">
          {itemIds.map((selectedItemId) => (
            <SelectedLegalEntity
              key={selectedItemId}
              name={options[selectedItemId]}
              onRemove={handleRemoveItem(selectedItemId)}
            />
          ))}
          <Box mt={2}>
            <Button onClick={handleClearAll} variation="subtle">
              <Typography color="primary">{tCommon(CLEAR_ALL)}</Typography>
            </Button>
          </Box>
        </Box>
      )}

      <Box mt={1} height={0} />
      <Typography variation="small" color="text.label" lineHeight={2}>
        {tProfile(CREDIT_CARD_LEGAL_ENTITY_HELP)}
      </Typography>
    </>
  );
};

export interface ILegalEntityFormProps {
  isCentralCard: boolean;
  itemIds: string[];
  setItemIds: (values: ISelectedItems) => void;
  organizationId?: string;
}

const LegalEntityFormContainer = ({
  isCentralCard,
  itemIds,
  setItemIds,
  organizationId,
}: ILegalEntityFormProps): JSX.Element | null => {
  if (!isCentralCard) {
    return null;
  }

  if (!organizationId) {
    return null;
  }

  return (
    <RBAC allowedRoles={userRolesByFeature.creditCardLegalEntities}>
      <LegalEntityForm itemIds={itemIds} setItemIds={setItemIds} organizationId={organizationId} />
    </RBAC>
  );
};

export default LegalEntityFormContainer;
